import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { DeliveryConfig } from "../../utils/deliveryQuote";
import { DELIVERY_ZONE_TEXTS } from "../shared/constants";

interface Props {
  config: DeliveryConfig | undefined;
  isSaving: boolean;
  onSave: (patch: DeliveryConfig) => void;
}

/**
 * The `__config__` row, which is a row but is not a municipality.
 *
 * It gets a card rather than a grid line because none of the grid's columns apply to it — it has no
 * fee, no zone and no delivery window — and because a settings row rendered among the municipalities
 * is a settings row somebody eventually sorts, filters away, or deletes.
 */
const DeliveryConfigCard: React.FC<Props> = ({ config, isSaving, onSave }) => {
  const [leadTime, setLeadTime] = useState("");
  const [priceHold, setPriceHold] = useState("");
  const [pickupEnabled, setPickupEnabled] = useState(true);
  const [pickupAddress, setPickupAddress] = useState("");

  // `0` is a legitimate value for both day counts ("same day", "no guarantee"), so these are
  // emptiness checks rather than the `?? ""` that would be enough for a string.
  useEffect(() => {
    if (!config) return;
    setLeadTime(
      config.default_lead_time_days === undefined || config.default_lead_time_days === null
        ? ""
        : String(config.default_lead_time_days),
    );
    setPriceHold(
      config.price_hold_days === undefined || config.price_hold_days === null
        ? ""
        : String(config.price_hold_days),
    );
    setPickupEnabled(config.pickup_enabled !== false);
    setPickupAddress(config.pickup_address ?? "");
  }, [config]);

  const handleSave = () => {
    onSave({
      default_lead_time_days: Number(leadTime),
      price_hold_days: Number(priceHold),
      pickup_enabled: pickupEnabled,
      pickup_address: pickupAddress,
    });
  };

  const isNumeric = (value: string) => value.trim() !== "" && Number.isFinite(Number(value));
  const canSave = isNumeric(leadTime) && isNumeric(priceHold) && !isSaving;

  return (
    <Card variant="outlined" sx={{ mt: 3 }} data-testid="delivery-config-card">
      <CardContent>
        <Typography variant="h6" fontWeight="bold" color="text.primary">
          {DELIVERY_ZONE_TEXTS.configTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {DELIVERY_ZONE_TEXTS.configSubtitle}
        </Typography>

        <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-start">
          <TextField
            size="small"
            type="number"
            label={DELIVERY_ZONE_TEXTS.configLeadTimeLabel}
            helperText={DELIVERY_ZONE_TEXTS.configLeadTimeHelp}
            value={leadTime}
            onChange={(e) => setLeadTime(e.target.value)}
            inputProps={{ min: 0 }}
          />
          <TextField
            size="small"
            type="number"
            label={DELIVERY_ZONE_TEXTS.configPriceHoldLabel}
            value={priceHold}
            onChange={(e) => setPriceHold(e.target.value)}
            inputProps={{ min: 0 }}
          />
          <TextField
            size="small"
            label={DELIVERY_ZONE_TEXTS.configAddressLabel}
            value={pickupAddress}
            onChange={(e) => setPickupAddress(e.target.value)}
            sx={{ minWidth: 320, flexGrow: 1 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={pickupEnabled}
                onChange={(e) => setPickupEnabled(e.target.checked)}
                inputProps={{ "aria-label": DELIVERY_ZONE_TEXTS.configPickupLabel }}
              />
            }
            label={DELIVERY_ZONE_TEXTS.configPickupLabel}
          />
        </Box>

        <Box mt={2}>
          <Button variant="contained" color="primary" disabled={!canSave} onClick={handleSave}>
            {isSaving ? DELIVERY_ZONE_TEXTS.configSaving : DELIVERY_ZONE_TEXTS.configSave}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DeliveryConfigCard;
